// ABOUTME: Utility functions for exporting database data in various formats (SQLite, CSV, JSON)
// ABOUTME: Handles conversion and download of data from storage providers

import { DataItem } from '../../types';
import { StorageProvider } from './types';

export type ExportFormat = 'sqlite' | 'csv' | 'json';

/**
 * Export data from a storage provider in the specified format
 */
export async function exportData(provider: StorageProvider, format: ExportFormat): Promise<void> {
    switch (format) {
        case 'sqlite':
            await exportSQLite(provider);
            break;
        case 'csv':
            await exportCSV(provider);
            break;
        case 'json':
            await exportJSON(provider);
            break;
        default:
            throw new Error(`Unsupported export format: ${format}`);
    }
}

/**
 * Export the SQLite database file directly
 */
async function exportSQLite(provider: StorageProvider): Promise<void> {
    // Check if provider has exportDatabase method
    if ('exportDatabase' in provider && typeof provider.exportDatabase === 'function') {
        const data = await provider.exportDatabase();
        downloadFile(data, 'workout-nutrition-data.sqlite3', 'application/x-sqlite3');
    } else {
        throw new Error('SQLite export is only supported with SQL.js storage provider');
    }
}

/**
 * Export data as CSV format
 */
async function exportCSV(provider: StorageProvider): Promise<void> {
    // Get all data from the provider
    const allItems = await getAllItems(provider);
    
    // Convert to CSV
    const csv = convertToCSV(allItems);
    const blob = new Blob([csv], { type: 'text/csv' });
    downloadFile(blob, 'workout-nutrition-data.csv', 'text/csv');
}

/**
 * Export data as JSON format
 */
async function exportJSON(provider: StorageProvider): Promise<void> {
    console.log('exportJSON called with provider:', provider.getName());
    try {
        // Get all data from the provider
        const allItems = await getAllItems(provider);
        console.log('Items to export:', allItems.length);
        
        // Convert to JSON
        const json = JSON.stringify(allItems, null, 2);
        console.log('JSON string length:', json.length);
        
        const blob = new Blob([json], { type: 'application/json' });
        console.log('Blob created, size:', blob.size);
        
        downloadFile(blob, 'workout-nutrition-data.json', 'application/json');
        console.log('Download initiated');
    } catch (error) {
        console.error('Error in exportJSON:', error);
        throw error;
    }
}

/**
 * Get all items from the storage provider
 */
async function getAllItems(provider: StorageProvider): Promise<DataItem[]> {
    console.log('Getting all items from provider:', provider.getName());
    const allItems: DataItem[] = [];
    const pageSize = 1000; // Large page size to minimize queries
    let page = 1;
    let hasMore = true;
    
    while (hasMore) {
        console.log(`Fetching page ${page} with pageSize ${pageSize}`);
        const items = await provider.getItems(page, pageSize);
        console.log(`Got ${items.length} items from page ${page}`);
        allItems.push(...items);
        
        // Check if we got a full page
        hasMore = items.length === pageSize;
        page++;
    }
    
    console.log(`Total items fetched: ${allItems.length}`);
    return allItems;
}

/**
 * Convert DataItem array to CSV format
 */
function convertToCSV(items: DataItem[]): string {
    if (items.length === 0) {
        return 'id,name,description,imageUrl,category,timestamp,data\n';
    }
    
    // Create CSV header
    const headers = ['id', 'name', 'description', 'imageUrl', 'category', 'timestamp'];
    
    // Find all unique data fields across all items
    const dataFields = new Set<string>();
    items.forEach(item => {
        if (item.data && typeof item.data === 'object') {
            Object.keys(item.data).forEach(key => dataFields.add(key));
        }
    });
    
    // Add data fields to headers
    const sortedDataFields = Array.from(dataFields).sort();
    headers.push(...sortedDataFields.map(field => `data.${field}`));
    
    // Create CSV rows
    const rows = items.map(item => {
        const row = [
            item.id,
            escapeCSV(item.name),
            escapeCSV(item.description || ''),
            escapeCSV(item.imageUrl || ''),
            escapeCSV(item.category || ''),
            item.timestamp || ''
        ];
        
        // Add data fields
        sortedDataFields.forEach(field => {
            if (item.data && typeof item.data === 'object') {
                const value = item.data[field];
                if (value !== undefined) {
                    row.push(escapeCSV(JSON.stringify(value)));
                } else {
                    row.push('');
                }
            } else {
                row.push('');
            }
        });
        
        return row.join(',');
    });
    
    return [headers.join(','), ...rows].join('\n');
}

/**
 * Escape CSV field values
 */
function escapeCSV(value: any): string {
    if (value === null || value === undefined) {
        return '';
    }
    
    const str = String(value);
    
    // If contains comma, newline, or double quote, wrap in quotes
    if (str.includes(',') || str.includes('\n') || str.includes('"')) {
        // Escape double quotes by doubling them
        return `"${str.replace(/"/g, '""')}"`;
    }
    
    return str;
}

/**
 * Download a file to the user's device
 */
function downloadFile(data: Blob | Uint8Array, filename: string, mimeType: string): void {
    console.log('downloadFile called:', { filename, mimeType, dataType: data.constructor.name });
    
    let blob: Blob;
    
    if (data instanceof Uint8Array) {
        blob = new Blob([data], { type: mimeType });
    } else {
        blob = data;
    }
    
    console.log('Creating download URL for blob of size:', blob.size);
    const url = URL.createObjectURL(blob);
    console.log('Download URL created:', url);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    console.log('Clicking download link');
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log('Download complete, URL revoked');
}